/** Helpers for Murf voice catalog (GEN2 / FALCON) from GET /v1/speech/voices. */

const LANGUAGE_TO_MURF_LOCALE = {
  en: 'en-US',
  hi: 'hi-IN',
  kn: 'kn-IN',
  mr: 'mr-IN',
  ta: 'ta-IN',
  te: 'te-IN',
  bn: 'bn-IN',
  es: 'es-ES',
  fr: 'fr-FR',
  de: 'de-DE',
  pt: 'pt-BR',
  ja: 'ja-JP',
  ko: 'ko-KR',
  zh: 'zh-CN',
  ar: 'ar-SA',
};

export function languageToMurfLocale(language) {
  const code = (language || 'en').trim().toLowerCase();
  if (LANGUAGE_TO_MURF_LOCALE[code]) return LANGUAGE_TO_MURF_LOCALE[code];
  if (code.includes('-')) return code;
  return `${code}-US`;
}

function localeLanguagePrefix(locale) {
  return (locale || '').trim().split('-')[0].toLowerCase();
}

export function voiceSupportsMurfLocale(voice, locale) {
  if (!voice || !locale) return true;
  const prefix = localeLanguagePrefix(locale);
  const primary = (voice.locale || '').toLowerCase();
  if (primary.startsWith(prefix)) return true;
  const supported = voice.supported_locales || {};
  return Object.keys(supported).some((loc) => loc.toLowerCase().startsWith(prefix));
}

export function bestMurfLocaleForVoice(voice, locale) {
  const target = (locale || '').trim() || languageToMurfLocale('en');
  const supported = voice?.supported_locales || {};
  if (supported[target]) return target;
  const prefix = localeLanguagePrefix(target);
  const match = Object.keys(supported).find((loc) => loc.toLowerCase().startsWith(prefix));
  if (match) return match;
  return voice?.locale || target;
}

/** Filter Murf voices that support the given project language code (en, hi, …). */
export function murfVoicesForLanguage(voices, language) {
  const locale = languageToMurfLocale(language);
  return (voices || [])
    .filter((v) => voiceSupportsMurfLocale(v, locale))
    .map((v) => ({
      ...v,
      effective_locale: bestMurfLocaleForVoice(v, locale),
    }));
}

/** Build locale dropdown options from Murf API `locales` array or voice catalog. */
export function extractMurfLocaleOptions(voices, apiLocales) {
  if (Array.isArray(apiLocales) && apiLocales.length) {
    return apiLocales.map((entry) => ({
      locale: entry.locale,
      label: entry.label || entry.locale,
      count: entry.count,
    }));
  }
  const map = new Map();
  for (const v of voices || []) {
    const supported = v.supported_locales || {};
    const entries = Object.keys(supported).length
      ? Object.entries(supported)
      : [[v.locale, { detail: v.display_language || v.locale }]];
    for (const [loc, meta] of entries) {
      if (!loc) continue;
      const label = meta?.detail || v.display_language || loc;
      if (!map.has(loc)) map.set(loc, { locale: loc, label, count: 0 });
      map.get(loc).count += 1;
    }
  }
  return [...map.values()].sort((a, b) => a.locale.localeCompare(b.locale));
}

export function filterMurfVoicesByLocale(voices, locale) {
  const loc = (locale || '').trim();
  if (!loc) return voices || [];
  return (voices || [])
    .filter((v) => voiceSupportsMurfLocale(v, loc))
    .map((v) => ({
      ...v,
      effective_locale: bestMurfLocaleForVoice(v, loc),
    }));
}

export function murfStylesForVoice(voice, locale) {
  if (!voice) return [];
  const loc = bestMurfLocaleForVoice(voice, locale || voice.locale || 'en-US');
  const supported = voice.supported_locales || {};
  const fromLocales = supported[loc]?.available_styles;
  const styles = (fromLocales && fromLocales.length)
    ? fromLocales
    : (voice.available_styles || []);
  return styles.map((s) => ({ id: String(s), label: String(s) }));
}

/** Build a lowercase haystack for free-text voice search. */
export function voiceSearchHaystack(voice) {
  if (!voice) return '';
  const parts = [
    voice.voice_id,
    voice.id,
    voice.name,
    voice.label,
    voice.display_language,
    voice.locale,
    voice.effective_locale,
    voice.language,
    voice.gender,
    voice.accent,
    voice.description,
  ];
  const supported = voice.supported_locales || {};
  for (const [loc, meta] of Object.entries(supported)) {
    parts.push(loc);
    if (meta && typeof meta === 'object') {
      parts.push(meta.detail, meta.label);
    }
  }
  return parts
    .filter((p) => p != null && String(p).trim())
    .join(' ')
    .toLowerCase();
}

/** Filter voices by name, id, locale, language, gender, accent, or any visible text. */
export function filterVoicesBySearch(voices, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return voices || [];
  const tokens = q.split(/\s+/).filter(Boolean);
  return (voices || []).filter((voice) => {
    const hay = voiceSearchHaystack(voice);
    return tokens.every((token) => hay.includes(token));
  });
}
